import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { Quat } from '../../engine/math/quat';
import { easeOutBack, clamp } from '../../engine/math/easing';
import type { GpuMesh } from '../../engine/render/GpuMesh';
import type { Ctx, Renderable } from '../Ctx';
import { ITEM_DEFS, ItemDef, ItemId } from './ItemDefs';
import type { Player } from '../player/Player';

export type ItemLoc = 'ground' | 'held' | 'placed' | 'magpie' | 'buried' | 'caged';

export interface ItemState {
  def: ItemDef;
  entity: number;
  t: Transform;
  rd: Renderable;
  loc: ItemLoc;
  vel: Vec3;
  sleeping: boolean;
  /** Gap marker id when placed. */
  placedAt: string | null;
  phase: number;
  /** Lantern only. */
  lit: boolean;
  /** Cannot be picked up by hand (caged behind bars). */
  locked: boolean;
  spin: number;
  /** Set once the player has picked it up; the Magpie only takes things you have handled. */
  touched: boolean;
}

const PICKUP_REACH = 1.9;
const GRAVITY = 22;

/**
 * Items are physical bodies: sphere vs. static world, bounce, sleep; one may be held (parented to the hand).
 * §4: E = pick up / swap / drop, F = use (handled by World via onUse).
 */
export class Items {
  readonly all: ItemState[] = [];
  held: ItemState | null = null;
  private meshes = new Map<ItemId, GpuMesh>();
  private grabT = 1;
  onPickup?: (it: ItemState) => void;
  onDrop?: (it: ItemState) => void;
  onUse?: (it: ItemState) => void;
  /** Called with the nearest interactable item (or null) every step, for the HUD hint. */
  onNearest?: (it: ItemState | null) => void;

  constructor(private readonly ctx: Ctx, private readonly player: Player) {}

  private mesh(id: ItemId): GpuMesh {
    let m = this.meshes.get(id);
    if (!m) { m = this.ctx.renderer.createMesh(ITEM_DEFS[id].mesh(), 'item.' + id); this.meshes.set(id, m); }
    return m;
  }

  spawn(id: ItemId, pos: Vec3, loc: ItemLoc = 'ground'): ItemState {
    const def = ITEM_DEFS[id];
    const t = new Transform();
    t.position.copy(pos);
    t.position.y = this.ctx.statics.ground(pos.x, pos.y + 1, pos.z, 0.5).y + def.groundY;
    t.rotation.copy(def.groundRot);
    t.snapPrev();
    const e = this.ctx.spawn('item.' + id, t, this.mesh(id));
    const rd = this.ctx.renderables.req(e);
    const it: ItemState = { def, entity: e, t, rd, loc, vel: new Vec3(), sleeping: true, placedAt: null, phase: Math.random() * 6, lit: false, locked: loc === 'caged', spin: 0, touched: false };
    if (loc === 'buried') rd.visible = false;
    this.all.push(it);
    return it;
  }

  get(id: ItemId): ItemState | undefined { return this.all.find((i) => i.def.id === id); }
  isHolding(id: ItemId): boolean { return this.held?.def.id === id; }

  /** World position of an item regardless of parenting. */
  worldPos(it: ItemState, out = new Vec3()): Vec3 {
    if (it.loc === 'held' || it.loc === 'magpie') return it.t.getWorldPosition(out);
    return out.copy(it.t.position);
  }

  /** Nearest item the player could interact with. */
  nearest(): ItemState | null {
    const p = this.player.position;
    let best: ItemState | null = null, bd = PICKUP_REACH;
    for (const it of this.all) {
      if (it.loc === 'held' || it.loc === 'magpie' || it.loc === 'buried' || it.locked) continue;
      const d = it.t.position.distanceToXZ(p);
      const dy = Math.abs(it.t.position.y - p.y);
      const reach = PICKUP_REACH + (it.def.heavy ? 0.6 : 0);
      if (d < reach && dy < 2.2 && d < bd + (it.def.heavy ? 0.6 : 0)) { best = it; bd = d; }
    }
    return best;
  }

  /** Detach from wherever it is and put it at a world position as a free body. */
  release(it: ItemState, pos: Vec3, vel?: Vec3): void {
    if (it.loc === 'held' && this.held === it) { this.held = null; this.player.holding = false; }
    it.t.setParent(null);
    it.t.position.copy(pos);
    it.t.rotation.copy(it.def.groundRot);
    it.t.scale.set(1, 1, 1);
    it.t.snapPrev();
    it.loc = 'ground';
    it.placedAt = null;
    it.vel.copy(vel ?? new Vec3());
    it.sleeping = false;
    it.rd.visible = true;
    it.spin = (Math.random() - 0.5) * 6;
  }

  pickup(it: ItemState): void {
    if (this.held) return;
    it.loc = 'held';
    it.placedAt = null;
    it.sleeping = true;
    it.rd.visible = true;
    it.t.setParent(this.player.hand);
    it.t.position.copy(it.def.handPos);
    it.t.rotation.copy(it.def.handRot);
    it.t.snapPrev();
    this.held = it;
    it.touched = true;
    this.player.holding = true;
    this.grabT = 0;
    this.ctx.sfx.pickup();
    this.onPickup?.(it);
  }

  /** Drop the held item in front of the player with a small toss. */
  drop(toss = 1): ItemState | null {
    const it = this.held;
    if (!it) return null;
    const p = this.player.position;
    const f = _v.set(Math.sin(this.player.facing), 0, Math.cos(this.player.facing));
    const pos = _p.copy(p).addScaled(f, 0.7);
    pos.y = p.y + 1.0;
    const vel = _v2.copy(f).scale(1.2 * toss);
    vel.y = 1.5 * toss;
    this.release(it, pos, vel);
    this.ctx.sfx.drop();
    this.onDrop?.(it);
    return it;
  }

  /** Drop at an explicit location (death, gate). */
  dropAt(pos: Vec3): ItemState | null {
    const it = this.held;
    if (!it) return null;
    this.release(it, _p.copy(pos).add(_v.set(0, 0.6, 0)), _v2.set((Math.random() - 0.5) * 1.5, 1.5, (Math.random() - 0.5) * 1.5));
    this.onDrop?.(it);
    return it;
  }

  /** E pressed: pick up / swap / drop (§3.1). */
  interact(): void {
    const near = this.nearest();
    if (!this.held) {
      if (near) this.pickup(near);
      else this.ctx.sfx.shake();
      return;
    }
    if (near && near !== this.held) {
      const dropped = this.drop(0.6);
      // swap: the dropped item lands where the new one was, roughly
      if (dropped) { dropped.t.position.copy(near.t.position); dropped.t.position.y += 0.3; dropped.t.snapPrev(); }
      this.pickup(near);
      this.ctx.sfx.swap();
      return;
    }
    this.drop();
  }

  update(dt: number): void {
    const st = this.ctx.statics;
    const time = this.ctx.time;
    for (const it of this.all) {
      // emissive pulse (§10)
      if (it.def.pulse > 0) {
        it.rd.emissiveMul = 1 + Math.sin((time / it.def.pulse) * Math.PI * 2 + it.phase) * 0.15;
      }
      if (it.loc === 'held') {
        if (this.grabT < 1) {
          this.grabT = Math.min(1, this.grabT + dt / 0.18);
          const k = easeOutBack(this.grabT);
          const s = 1.3 - 0.3 * k;
          it.t.scale.set(s, s, s);
          it.t.position.copy(it.def.handPos).addScaled(_v.set(0, -0.35, 0.2), 1 - k);
        }
        if (it.def.id === 'lantern') {
          // pendulum swing, damped by movement
          const sp = Math.hypot(this.player.velocity.x, this.player.velocity.z);
          const amp = clamp(0.35 - sp * 0.03, 0.08, 0.35);
          it.t.rotation.copy(it.def.handRot).multiply(_q.setEuler(0, Math.sin(time * (Math.PI * 2) / 1.2) * amp, Math.cos(time * 1.9) * amp * 0.4));
        }
        continue;
      }
      if (it.loc !== 'ground' || it.sleeping) continue;
      // free body: sphere vs world
      it.vel.y -= GRAVITY * dt;
      it.t.position.addScaled(it.vel, dt);
      st.resolveCapsule(it.t.position, it.def.radius * 0.6, 0.2, 0.1, _push);
      const g = st.ground(it.t.position.x, it.t.position.y + 0.3, it.t.position.z, 0.5, _ground);
      const floorY = g.y + it.def.groundY;
      if (it.t.position.y <= floorY) {
        it.t.position.y = floorY;
        if (it.vel.y < -1.0) {
          it.vel.y = -it.vel.y * 0.3;
          it.vel.x *= 0.6; it.vel.z *= 0.6;
          this.ctx.sfx.drop(it.t.position);
          this.onBounce?.(it);
        } else {
          it.vel.set(0, 0, 0);
          it.sleeping = true;
          it.t.rotation.copy(it.def.groundRot);
          it.spin = 0;
        }
      }
      if (!it.sleeping) it.t.rotation.copy(it.def.groundRot).multiply(_q.setEuler(it.spin * dt * 20, 0, 0));
    }
  }
  onBounce?: (it: ItemState) => void;

  /** Metal items (not held) within r of p, for the Magnet. */
  metalNear(p: Vec3, r: number): ItemState[] {
    const out: ItemState[] = [];
    for (const it of this.all) {
      if (!it.def.metal || it.loc === 'held' || it.loc === 'placed') continue;
      const wp = it.loc === 'magpie' ? it.t.getWorldPosition(_v) : it.t.position;
      if (wp.distanceTo(p) < r) out.push(it);
    }
    return out;
  }

  /** Magnet pulse on one item: erupt from sand, pop out of a cage, or fly to the player's feet. */
  pull(it: ItemState, toward: Vec3): 'erupt' | 'uncage' | 'pull' | 'magpie' {
    const kind = it.loc === 'buried' ? 'erupt' : it.loc === 'caged' ? 'uncage' : it.loc === 'magpie' ? 'magpie' : 'pull';
    if (kind === 'magpie') return kind;
    const from = it.t.position.clone();
    const dir = _v.copy(toward).sub(from); dir.y = 0;
    const d = dir.length();
    if (d > 1e-3) dir.scale(1 / d);
    const vel = _v2.copy(dir).scale(Math.min(9, d * 2.2));
    vel.y = kind === 'erupt' ? 7 : kind === 'uncage' ? 5 : 3;
    if (kind === 'uncage') from.y += 2.4; // over the bars
    this.release(it, from, vel);
    it.locked = false;
    return kind;
  }

  /** Serializable snapshot. */
  serialize(): { id: ItemId; pos: [number, number, number]; loc: ItemLoc; placedAt: string | null; lit: boolean; locked: boolean; touched: boolean }[] {
    return this.all.map((it) => {
      const p = this.worldPos(it);
      return { id: it.def.id, pos: [p.x, p.y, p.z], loc: it.loc, placedAt: it.placedAt, lit: it.lit, locked: it.locked, touched: it.touched };
    });
  }
}
const _v = new Vec3(), _v2 = new Vec3(), _p = new Vec3(), _push = new Vec3(), _q = new Quat();
const _ground = { y: 0, normal: new Vec3(0, 1, 0), floor: null };
